import { useEffect, useRef, useState } from "react";
import {
  Outlet,
  Link,
  createRootRoute,
  HeadContent,
  Scripts,
  useRouterState,
} from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { toast } from "sonner";

import { Toaster } from "@/components/ui/sonner";
import { OnlineStatusBanner } from "@/components/OnlineStatusBanner";
import { ErrorFallback } from "@/components/ErrorFallback";
import { supabase } from "@/integrations/supabase/client";
import { redeemInviteCode } from "@/lib/invite-codes.functions";
import { registerReferralAfterSignup } from "@/lib/referrals.functions";
import { getReferralCode, clearReferralCode, getOrCreateDeviceId } from "@/lib/referral";
import {
  initAnalytics,
  setUserId,
  setUserProperties,
  trackPageView,
} from "@/lib/analytics";
import { initErrorReporting } from "@/lib/errorReporting";
import { initMetaPixel, trackMetaPageView } from "@/lib/metaPixel";
import { queryClientDefaults } from "@/lib/queryClient";
import appCss from "../styles.css?url";

const INVITE_CODE_STORAGE_KEY = "pending_invite_code";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

const GOOGLE_SITE_VERIFICATION =
  (import.meta.env.VITE_GOOGLE_SITE_VERIFICATION as string | undefined) ||
  "RTNOiFs_pRm8dCGcPq9JczFIeQQ3UWHr3YK1J5878jc";

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      ...(GOOGLE_SITE_VERIFICATION
        ? [{ name: "google-site-verification", content: GOOGLE_SITE_VERIFICATION }]
        : []),
      // Per-route head() overrides title/description/og below. These act as defaults.
      { title: "KAGEM - AIで作る変身動画" },
      { name: "description", content: "日本語対応のAI変身動画作成サービス。写真と動画をアップロードするだけで、簡単に高品質な変身動画を生成。" },
      { property: "og:site_name", content: "KAGEM" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "ja_JP" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@kagem" },
      // PWA / mobile webapp
      { name: "theme-color", content: "#0A0A0F" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "KAGEM" },
      { name: "mobile-web-app-capable", content: "yes" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Noto+Sans+JP:wght@300;400;500;700&family=Montserrat:wght@200;300;400&family=Chakra+Petch:wght@300;400;500;700&family=Michroma&display=swap",
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
      // PWA manifest + apple touch icon
      { rel: "manifest", href: "/manifest.json" },
      { rel: "apple-touch-icon", href: "/icons/icon-180.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/icons/icon-72.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icons/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icons/icon-512.png" },
      { rel: "shortcut icon", href: "/icons/icon-192.png" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "KAGEM",
          url: "https://kagem.ai",
          logo: "https://kagem.ai/icons/icon-512.png",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "KAGEM",
          url: "https://kagem.ai",
          inLanguage: "ja-JP",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ({ error, reset }) => <ErrorFallback error={error as Error} reset={reset} />,
});

const THEME_INIT_SCRIPT = `(function(){try{var t=localStorage.getItem('kagem-theme');if(!t){t=window.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light';}if(t==='dark'){document.documentElement.classList.add('dark');}}catch(e){}})();`;

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="ja">
      <head>
        <HeadContent />
      </head>
      <body>
        <script dangerouslySetInnerHTML={{ __html: THEME_INIT_SCRIPT }} />
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const consumingRef = useRef(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [queryClient] = useState(() => new QueryClient({ defaultOptions: queryClientDefaults }));

  // Init global error reporting (window.onerror + unhandledrejection) once
  useEffect(() => {
    initErrorReporting();
  }, []);

  // GA4 init (一度だけ)
  useEffect(() => {
    initAnalytics();
  }, []);

  // Meta Pixel init (一度だけ) — 初回 PageView は init 内で発火
  useEffect(() => {
    initMetaPixel();
  }, []);

  // SPA ページビュートラッキング
  const isFirstPathnameRef = useRef(true);
  useEffect(() => {
    if (!pathname) return;
    trackPageView(pathname);
    // Meta Pixel: 初回は init で発火済みなのでスキップ
    if (isFirstPathnameRef.current) {
      isFirstPathnameRef.current = false;
    } else {
      trackMetaPageView();
    }
  }, [pathname]);

  // ログイン/ログアウトに応じて user_id を更新し、plan を user_properties に設定
  useEffect(() => {
    const updateUserContext = async (userId: string | null) => {
      setUserId(userId);
      if (!userId) return;
      try {
        const { data } = await supabase
          .from("profiles")
          .select("plan")
          .eq("user_id", userId)
          .maybeSingle();
        if (data?.plan) setUserProperties({ plan: data.plan });
      } catch {
        // ignore
      }
    };

    supabase.auth.getSession().then(({ data }) => {
      void updateUserContext(data.session?.user?.id ?? null);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      void updateUserContext(session?.user?.id ?? null);
    });
    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // After Google OAuth callback, consume any pending invite code.
    // If consumption fails (code already used by someone else, etc.),
    // sign the user out so they can't access the app without a valid code.
    const consume = async (userId: string) => {
      if (consumingRef.current) return;
      const code =
        typeof window !== "undefined"
          ? sessionStorage.getItem(INVITE_CODE_STORAGE_KEY)
          : null;
      if (!code) return;
      consumingRef.current = true;
      try {
        const result = await redeemInviteCode({ data: { code } });
        sessionStorage.removeItem(INVITE_CODE_STORAGE_KEY);
        if (!result.ok) {
          toast.error(result.message);
          await supabase.auth.signOut();
          return;
        }
        toast.success("ようこそ！登録が完了しました");
      } catch {
        sessionStorage.removeItem(INVITE_CODE_STORAGE_KEY);
        toast.error("招待コードの確認に失敗しました");
        await supabase.auth.signOut();
      } finally {
        consumingRef.current = false;
      }
      // Touch userId to satisfy the linter (we may use it in the future for logging).
      void userId;
    };

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        void consume(session.user.id);
        // Best-effort referral registration (no-op if no pending code or already referred).
        const refCode = getReferralCode();
        if (refCode) {
          void registerReferralAfterSignup({
            data: { code: refCode, deviceId: getOrCreateDeviceId() || null },
          })
            .catch(() => null)
            .finally(() => clearReferralCode());
        }
      }
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <OnlineStatusBanner />
      <Outlet />
      <Toaster richColors position="top-center" />
    </QueryClientProvider>
  );
}
